import apiClient from '../api/api';

// TypeScript interfaces
export interface SSOProvider {
  _id: string;
  issuer: string;
  authorization_endpoint: string;
  userinfo_endpoint: string;
  token_endpoint: string;
  provider_name: string;
  display_name: string;
  logo_url: string;
  client_id: string;
  client_secret: string;
  enable: boolean;
  is_active: boolean;
  standard_type?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CreateSSOProviderData {
  standard_type: string;
  issuer: string;
  authorization_endpoint: string;
  userinfo_endpoint: string;
  token_endpoint: string;
  provider_name: string;
  display_name: string;
  logo_url: string;
  client_id: string;
  client_secret: string;
  enable: boolean;
}

export interface UpdateSSOProviderData extends CreateSSOProviderData { }

export interface SSOProviderListParams {
  skip: number;
  limit: number;
  provider_name?: string;
  display_name?: string;
}

export interface SSOProviderListResponse {
  success: boolean;
  code: number;
  data: {
    login_provider_list: SSOProvider[];
    total_count: number;
  };
}

export interface SSOProviderResponse {
  success: boolean;
  code: number;
  data: SSOProvider;
}

export interface SSOProviderStats {
  total_providers: number;
  active_providers: number;
  inactive_providers: number;
}

class SSOProvidersService {
  private readonly BASE_PATH = '/dms-v2-srv/login-provider-srv';

  // GET: Fetch list with pagination and filters
  async getProviders(params: SSOProviderListParams): Promise<SSOProviderListResponse> {
    try {
      const response = await apiClient.post(`${this.BASE_PATH}/utils/list`, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching SSO providers:', error);
      throw error;
    }
  }

  // GET: Fetch single provider by ID
  async getProviderById(id: string): Promise<SSOProvider> {
    try {
      const response = await apiClient.get(`${this.BASE_PATH}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching SSO provider ${id}:`, error);
      throw error;
    }
  }

  // POST: Create new provider
  async createProvider(providerData: CreateSSOProviderData): Promise<SSOProvider> {
    try {
      const response = await apiClient.post(this.BASE_PATH, providerData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating SSO provider:', error);
      throw error;
    }
  }

  // PUT: Update existing provider
  async updateProvider(id: string, providerData: UpdateSSOProviderData): Promise<SSOProvider> {
    try {
      const response = await apiClient.put(`${this.BASE_PATH}/${id}`, providerData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating SSO provider ${id}:`, error);
      throw error;
    }
  }

  // DELETE: Remove provider
  async deleteProvider(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.BASE_PATH}/${id}`);
    } catch (error) {
      console.error(`Error deleting SSO provider ${id}:`, error);
      throw error;
    }
  }

  // PATCH: Toggle provider enable status
  async toggleProviderStatus(id: string, enable: boolean): Promise<SSOProvider> {
    try {
      const response = await apiClient.put(`${this.BASE_PATH}/${id}`, { enable });
      return response.data.data;
    } catch (error) {
      console.error(`Error toggling SSO provider ${id} status:`, error);
      throw error;
    }
  }

  // GET: Fetch provider stats (calculated from list)
  async getProviderStats(): Promise<SSOProviderStats> {
    try {
      const response = await this.getProviders({ skip: 0, limit: 1000 }); // Get all for stats
      const providers = response.data.login_provider_list;

      return {
        total_providers: providers.length,
        active_providers: providers.filter(p => p.enable && p.is_active).length,
        inactive_providers: providers.filter(p => !p.enable || !p.is_active).length,
      };
    } catch (error) {
      console.error('Error fetching SSO provider stats:', error);
      throw error;
    }
  }
}

export const ssoProvidersService = new SSOProvidersService();
export default ssoProvidersService;